import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useTranslation } from 'react-i18next'
import type { CommandKey, ContextOrg, ContextSite, PayloadOf } from '@gk/contracts'
import { useAuth } from '../auth/AuthProvider.tsx'
import { authClient } from '../auth/client.ts'
import { evaluatePlatform, type PlatformInfo } from '../platform/storage.ts'
import { dbName, GkDb, localOnlyTables, setMeta } from '../sync/db.ts'
import { SyncEngine, type EngineState } from '../sync/engine.ts'
import { startAutoSync } from '../sync/scheduler.ts'
import { enqueue, type EnqueueOptions } from '../sync/outbox.ts'
import { settleTransient } from '../sync/transient.ts'
import { PhotoLane } from '../photos/lane.ts'
import { localDate } from '../lib/time.ts'
import { restTransport, type Transport } from '../sync/transport.ts'
import { setLanguage, setLanguageTemporarily } from '../i18n.ts'
import type { Locale } from '@gk/i18n'
import { KIOSK_PERMISSIONS, rolesAt } from './roles.ts'
import { startDiagnostics, watchTransport } from '../diagnostics/wire.ts'
import { setDiagnosticContext } from '../diagnostics/reporter.ts'

export interface CommandVerdict { status: 'applied' | 'applied_with_exception' | 'rejected' | 'conflict' | 'error' | 'queued'; code: string | null; exceptions: string[]; commandId: string; /** What the server's handler said of the command (its `summary`, for a screen that reports counts). */ result?: Record<string, unknown> | null }
const SETTLED = new Set(['applied', 'applied_with_exception', 'rejected', 'conflict', 'error'])
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

export const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0-m1'

/** A shared workstation (ADR-070): the device is the principal, a person is only ever "unlocked" on it, by a PIN the SERVER verifies. */
export interface KioskSession { unlockId: string; personId: string; displayName: string; locale: Locale }
export interface KioskPerson { person_id: string; display_name: string; has_pin: boolean }
export type KioskRefusal = { ok: false; code: string; remaining?: number }
export interface KioskApi {
  deviceId: string
  session: KioskSession | null
  roster(): Promise<KioskPerson[]>
  unlock(personId: string, pin: string): Promise<{ ok: true } | KioskRefusal>
  /** The screen locks at once; what the session sent is flushed, then the server is told. */
  lock(reason: 'finished' | 'not_me' | 'idle'): Promise<void>
  setPin(personId: string, proof: { kind: 'pin' | 'code'; value: string }, newPin: string): Promise<{ ok: true } | KioskRefusal>
}

export interface ClubApi {
  org: ContextOrg; site: ContextSite
  /** The person acting here; null on a shared workstation (see `kiosk.session`). */
  personId: string | null
  kiosk: KioskApi | null
  /** Written by ClubInner, read by `send`: the open session of a workstation. */
  kioskSessionRef: { current: KioskSession | null }
  db: GkDb; engine: SyncEngine; deviceId: string; platform: PlatformInfo; transport: Transport
  syncSoon(): void
  /** Record a confirmed action: one local transaction (outbox + projection), then a prompt sync. Throws ZodError on a bad payload. */
  send<K extends CommandKey>(key: K, payload: PayloadOf<K>, opts?: EnqueueOptions): Promise<{ commandId: string }>
  /** For the online-first screens (the workshop): record the command, sync now and return the SERVER's verdict, so a refusal shows where
   *  the user tapped. Offline it returns 'queued' (the command is safe and goes when the network returns). A refusal shown here is
   *  dismissed from the outbox so it is not reported twice. */
  sendAndWait<K extends CommandKey>(key: K, payload: PayloadOf<K>, opts?: EnqueueOptions): Promise<CommandVerdict>
  /** A single-use server nonce for one online-only command. Call it right after the second-factor challenge. */
  beginOnline(): Promise<string>
  can(permission: string): boolean
}
const Ctx = createContext<ClubApi | null>(null)
export const useClub = (): ClubApi => { const v = useContext(Ctx); if (!v) throw new Error('useClub outside ClubGate'); return v }
export function useEngineState(): EngineState {
  const { engine } = useClub()
  return useSyncExternalStore((cb) => engine.subscribe(cb), () => engine.state)
}

/** The clubs this login belongs to, and the way back to the club chooser: the choice is remembered per login on this device (`gk.org.<uid>`), a sign-out
 *  keeps it, so a login of several clubs changes club here (Réglages). Null outside ClubGate. */
export interface ClubChoice { clubs: Array<{ org_id: string; org_name: string }>; change(): void }
const ChoiceCtx = createContext<ClubChoice | null>(null)
export const useClubChoice = (): ClubChoice | null => useContext(ChoiceCtx)

const transport = restTransport(authClient)
const ctxKey = (uid: string) => `gk.ctx.${uid}`
const orgKey = (uid: string) => `gk.org.${uid}`

/** Which clubs does this login belong to? Online: ask the server and cache the answer. Offline: use the cache (a device
 *  that has never been online cannot know, and says so). */
async function loadContext(uid: string): Promise<{ orgs: ContextOrg[]; fromCache: boolean } | { error: string }> {
  try {
    const orgs = await transport.myContext()
    try { localStorage.setItem(ctxKey(uid), JSON.stringify(orgs)) } catch { /* ignore */ }
    return { orgs, fromCache: false }
  } catch (e) {
    try {
      const cached = localStorage.getItem(ctxKey(uid))
      if (cached) return { orgs: JSON.parse(cached) as ContextOrg[], fromCache: true }
    } catch { /* ignore */ }
    return { error: e instanceof Error ? e.message : String(e) }
  }
}

export function ClubGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { userId, signOut } = useAuth()
  const [orgs, setOrgs] = useState<ContextOrg[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [orgId, setOrgId] = useState<string | null>(null)
  const [platform, setPlatform] = useState<PlatformInfo | null>(null)
  const [club, setClub] = useState<ClubApi | null>(null)

  useEffect(() => { void evaluatePlatform().then(setPlatform) }, [])
  useEffect(() => {
    if (!userId) return
    let live = true
    void loadContext(userId).then((r) => {
      if (!live) return
      if ('error' in r) { setError(r.error); return }
      setOrgs(r.orgs)
      const saved = (() => { try { return localStorage.getItem(orgKey(userId)) } catch { return null } })()
      const pick = r.orgs.find((o) => o.org_id === saved) ?? (r.orgs.length === 1 ? r.orgs[0] : undefined)
      if (pick) setOrgId(pick.org_id)
    })
    return () => { live = false }
  }, [userId])

  const org = orgs?.find((o) => o.org_id === orgId) ?? null
  const site = org?.sites[0] ?? null     // M1: the first site; a site switcher arrives when a club has more than one
  const openOrgId = org?.org_id ?? null

  // M11: an error captured before the club's reporter starts waits under this login and club only, and goes to no other (diagnostics/reporter.ts); with no club open it
  // is dropped (the sign-in page, the club chooser, after a sign-out)
  useEffect(() => {
    setDiagnosticContext(userId && openOrgId ? { userId, orgId: openOrgId } : null)
    return () => { setDiagnosticContext(null) }
  }, [userId, openOrgId])

  useEffect(() => {
    if (!userId || !org || !site || !platform) return
    const db = new GkDb(dbName(userId, org.org_id))
    const laneRef: { current: PhotoLane | null } = { current: null }
    // the engine meets the server through watchTransport: what fails there (not the network being away) becomes a diagnostic (M11)
    const engine = new SyncEngine({ db, transport: watchTransport(transport), orgId: org.org_id, appVersion: APP_VERSION, platform: () => ({ persisted: platform.persisted, standalone: platform.standalone }), afterSync: () => laneRef.current?.run() ?? Promise.resolve() })
    const auto = startAutoSync(engine)
    let stopped = false
    let stopDiagnostics: (() => void) | null = null
    const kioskSessionRef: ClubApi['kioskSessionRef'] = { current: null }
    // a workstation uses the device id it was enrolled with (the server knows no other)
    void (org.kiosk_device_id ? setMeta(db, 'deviceId', org.kiosk_device_id) : Promise.resolve()).then(() => engine.deviceId()).then((deviceId) => {
      if (stopped) return
      const send: ClubApi['send'] = async (key, payload, opts) => {
        const k = org.kiosk_device_id ? kioskSessionRef.current : null
        if (org.kiosk_device_id && !k) throw new Error('KIOSK_LOCKED')
        const row = await enqueue(db, key, payload, {
          orgId: org.org_id, siteId: site.site_id, personId: k ? k.personId : org.person_id!, deviceId, appVersion: APP_VERSION,
          ...(k ? { authMode: 'kiosk' as const, kioskUnlockId: k.unlockId } : {}),
        }, opts)
        auto.soon()
        return { commandId: row.command_id }
      }
      // M16-I1: the lane also fetches ahead the pictures of the procedures of the person's assigned jobs (the workstation's unlocked person, when one is)
      laneRef.current = new PhotoLane({
        db, transport, send: (key, payload, opts) => send(key, payload, opts), syncSoon: auto.soon,
        person: () => (org.kiosk_device_id ? kioskSessionRef.current?.personId ?? null : org.person_id ?? null), today: () => localDate(new Date(), site.timezone),
      })
      // M11: what goes wrong on this device, scrubbed, queued and sent after each sync (diagnostics/reporter.ts); one queue per login and club, like the database
      stopDiagnostics = startDiagnostics({ engine, transport, db, userId, orgId: org.org_id, deviceId, appVersion: APP_VERSION, storageKey: dbName(userId, org.org_id) })
      setClub({
        org, site, personId: org.person_id, kiosk: null, kioskSessionRef, db, engine, deviceId, platform, transport, syncSoon: auto.soon,
        send,
        beginOnline: () => transport.beginOnlineAction(org.org_id, deviceId),
        async sendAndWait(key, payload, opts) {
          const { commandId } = await send(key, payload, opts)
          const deadline = Date.now() + 20_000
          while (Date.now() < deadline) {
            await engine.sync({ force: true })        // single-flight: if a cycle was already running, loop and run another
            const row = await db.outbox.get(commandId)
            if (!row) return { status: 'queued', code: null, exceptions: [], commandId }
            if (SETTLED.has(row.status)) {
              if (row.status === 'rejected' || row.status === 'conflict' || row.status === 'error') {
                // a refusal the screen has shown goes; one of sync/transient.ts leaves the device at once (as Sync.tsx's dismissal does)
                await db.transaction('rw', [db.outbox, db.overlay, ...localOnlyTables(db)], async () => { await db.outbox.update(commandId, { status: 'discarded' }); await db.overlay.where('command_id').equals(commandId).delete(); await settleTransient(db, commandId) })
              }
              return { status: row.status as CommandVerdict['status'], code: row.server_code ?? null, exceptions: row.exceptions ?? [], commandId, result: (row.server_result as Record<string, unknown> | null | undefined) ?? null }
            }
            if (engine.state.online === false || engine.state.authRequired) break
            await sleep(250)
          }
          return { status: 'queued', code: null, exceptions: [], commandId }
        },
        can: () => false,
      })
    })
    if (org.preferred_locale === 'fr' || org.preferred_locale === 'en') { if (!localStorage.getItem('gk.lang')) setLanguage(org.preferred_locale as Locale) }
    return () => { stopped = true; auto.stop(); stopDiagnostics?.(); setClub(null); db.close() }
  }, [userId, org, site, platform])

  const choice = useMemo<ClubChoice>(() => ({
    clubs: (orgs ?? []).map((o) => ({ org_id: o.org_id, org_name: o.org_name })),
    change: () => { try { if (userId) localStorage.removeItem(orgKey(userId)) } catch { /* ignore */ } setOrgId(null) },
  }), [orgs, userId])

  if (error) return <Centered><p>{t('errors.network')}</p><p className="text-sm opacity-70">{error}</p></Centered>
  if (!orgs || !platform) return <Centered>{t('common.loading')}</Centered>
  if (orgs.length === 0) return <Centered><p>{t('auth.noAccess')}</p><button className="btn" onClick={() => void signOut()}>{t('auth.signOut')}</button></Centered>
  if (!org) {
    return (
      <Centered>
        <h1 className="text-xl font-semibold">{t('auth.chooseClub')}</h1>
        {orgs.map((o) => (
          <button key={o.org_id} className="btn w-full" onClick={() => { try { localStorage.setItem(orgKey(userId!), o.org_id) } catch { /* ignore */ } setOrgId(o.org_id) }}>{o.org_name}</button>
        ))}
      </Centered>
    )
  }
  if (!club || !site) return <Centered>{t('common.loading')}</Centered>
  return <ChoiceCtx.Provider value={choice}><ClubInner club={club}>{children}</ClubInner></ChoiceCtx.Provider>
}

/** Cosmetic permission gating from the device's own mirror (the server decides, always): the roles come from my_context (a person) or from the
 *  unlocked person's memberships (a workstation), the role -> permission matrix from the synced core.role_permission rows. A workstation never
 *  offers more than its crew-level field facts. */
function ClubInner({ club, children }: { club: ClubApi; children: ReactNode }) {
  const isKiosk = !!club.org.kiosk_device_id
  const [session, setSession] = useState<KioskSession | null>(null)
  const memberships = useLiveQuery(async (): Promise<Array<Record<string, unknown>>> => (isKiosk ? ((await club.db.membership.toArray()) as Array<Record<string, unknown>>) : []), [club, isKiosk], [] as Array<Record<string, unknown>>)
  const perms = useLiveQuery(async () => {
    const roles = isKiosk ? rolesAt(memberships, session?.personId, new Date()) : new Set(club.site.roles)
    const rp = await club.db.role_permission.toArray()
    return new Set(rp.filter((r) => roles.has(r.role_code as string)).map((r) => r.permission_code as string))
  }, [club, isKiosk, memberships, session], new Set<string>())

  const orgLocale: Locale = club.org.default_locale === 'en' ? 'en' : 'fr'
  const open = useCallback((s: KioskSession | null) => {
    club.kioskSessionRef.current = s
    setSession(s)
    setLanguageTemporarily(s ? s.locale : orgLocale)
  }, [club, orgLocale])

  const kiosk = useMemo<KioskApi | null>(() => {
    if (!club.org.kiosk_device_id) return null
    const args = { p_org: club.org.org_id, p_device: club.deviceId }
    return {
      deviceId: club.deviceId, session,
      roster: () => club.transport.rpc<KioskPerson[]>('kiosk_roster', args),
      async unlock(personId, pin) {
        const r = await club.transport.rpc<{ ok: boolean; code?: string; remaining?: number; unlock_id?: string; person_id?: string; display_name?: string; preferred_locale?: string }>('kiosk_unlock', { ...args, p_person: personId, p_pin: pin })
        if (!r.ok) return { ok: false, code: r.code ?? 'UNKNOWN', ...(r.remaining !== undefined ? { remaining: r.remaining } : {}) }
        open({ unlockId: r.unlock_id!, personId: r.person_id!, displayName: r.display_name ?? '', locale: r.preferred_locale === 'en' ? 'en' : 'fr' })
        return { ok: true }
      },
      async lock(reason) {
        const s = club.kioskSessionRef.current
        open(null)
        if (!s) return
        try { await club.engine.sync({ force: true }) } catch { /* what could not be sent now stays queued with its session id */ }
        try { await club.transport.rpc('kiosk_lock', { ...args, p_unlock: s.unlockId, p_reason: reason }) } catch { /* the next unlock closes it ('new_unlock') */ }
      },
      async setPin(personId, proof, newPin) {
        const r = await club.transport.rpc<{ ok: boolean; code?: string; remaining?: number }>('kiosk_set_pin', { ...args, p_person: personId, p_proof_kind: proof.kind, p_proof: proof.value, p_new_pin: newPin })
        return r.ok ? { ok: true } : { ok: false, code: r.code ?? 'UNKNOWN', ...(r.remaining !== undefined ? { remaining: r.remaining } : {}) }
      },
    }
  }, [club, session, open])

  const value = useMemo<ClubApi>(() => ({
    ...club, kiosk, personId: isKiosk ? (session?.personId ?? null) : club.personId,
    org: isKiosk && session ? { ...club.org, display_name: session.displayName } : club.org,      // the greeting is the unlocked person's, never the workstation's label
    can: (p: string) => perms.has(p) && (!isKiosk || KIOSK_PERMISSIONS.has(p)),
  }), [club, kiosk, isKiosk, session, perms])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

function Centered({ children }: { children: ReactNode }) {
  return <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 p-6 text-center">{children}</main>
}
